import React, { useCallback } from 'react'
import SeedClassDetailsForm, { FormData } from './SeedClassDetailsForm/SeedClassDetailsForm'
import * as Layout from 'views/layouts/MainLayout/MainLayout'
import * as Header from 'views/ui/Header/Header'
import { useSeedClass } from 'modules/domain/seedClass/hooks'
import { useParams } from 'react-router-dom'
import { Progress } from 'modules/types'
import { useSelector } from 'react-redux'
import SeedClassSelectors from 'modules/domain/seedClass/selectors'
import SeedClassActions from 'modules/domain/seedClass/duck'
import Routes from './routes'
import { useTranslation } from 'react-i18next'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import Item404 from 'views/layouts/Item404/Item404'
import ItemGenericError from 'views/layouts/ItemGenericError/ItemGenericError'
import { useAction, useHelmet, useHistoryPush } from '@agro-club/frontend-shared'

const SeedClassEdit: React.FC = () => {
  const params = useParams<{ id: string }>()
  const push = useHistoryPush()
  const updateSeedClassAction = useAction(SeedClassActions.updateRequested)
  const removeSeedClassAction = useAction(SeedClassActions.removeRequested)
  const page = useSelector(SeedClassSelectors.page)
  const { t } = useTranslation('seedClass')

  const handleSubmit = useCallback(
    (data: FormData) => {
      updateSeedClassAction(params.id, {
        name: data.name,
        crop_id: data.crop_id,
      })
    },
    [params.id, updateSeedClassAction],
  )

  const handleRemove = () => {
    removeSeedClassAction(params.id)
  }

  const [fetchProgress, seedClass] = useSeedClass(params.id)

  const meta = useSelector((state) => SeedClassSelectors.meta(state, params.id))

  const goBack = () => push({ route: Routes.List, query: { page } })

  useHelmet({ title: t('seedClassesMetaTitle') })

  const loading = () => <ItemLoadingLayout id={params.id} onBack={goBack} />
  const error404 = () => <Item404 id={params.id} onBack={goBack} title={t('errors.notFoundTitle')} />
  const errorUnknown = () => <ItemGenericError id={params.id} onBack={goBack} title={t('errors.unknownErrorTitle')} />

  if (fetchProgress === Progress.ERROR) {
    if (meta.fetchError === 'not_found') {
      return error404()
    }
    return errorUnknown()
  }

  if (fetchProgress === Progress.WORK || !seedClass) {
    return loading()
  }

  return (
    <>
      <Layout.Header>
        <Header.Root onClickBack={goBack}>
          <Header.Title size={'small'} title={seedClass.name} />
        </Header.Root>
      </Layout.Header>
      <Layout.Content>
        <SeedClassDetailsForm
          onCancel={goBack}
          onRemove={handleRemove}
          mode={'edit'}
          progress={meta.updateProgress}
          removeProgress={meta.removeProgress}
          initialValues={{
            name: seedClass.name || '',
            crop_id: seedClass.crop_id || '',
          }}
          onSubmit={handleSubmit}
        />
      </Layout.Content>
    </>
  )
}

export default SeedClassEdit
